import AnsiToHTML from "ansi-to-html";
import { reactive } from "vue";

const { csrf: csrfToken } = window.config;

// POST helper shared by the actions run view components
export const actionsFetch = (url, body, signal) => {
  return window.fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Csrf-Token": csrfToken,
    },
    body,
    signal,
  });
};

// Log line command prefix map
// Currently unused
export const LogLinePrefixCommandMap = {
  "::group::": "group",
  "##[group]": "group",
  "::endgroup::": "endgroup",
  "##[endgroup]": "endgroup",
  "##[error]": "error",
  "[command]": "command",
  "::add-matcher::": "hidden",
  "##[add-matcher]": "hidden",
  "::remove-matcher": "hidden",
};

export const parseLineCommand = (msg) => {
  // for (const [prefix, cmd] of Object.entries(LogLinePrefixCommandMap)) {
  //   if (msg.startsWith(prefix)) {
  //     return { cmd, prefix };
  //   }
  // }
  return { cmd: null, prefix: null };
};

export const createLogLineMessage = (msg, cmd) => {
  const logMessage = document.createElement("div");
  logMessage.className = "log-msg";
  if (cmd) {
    logMessage.classList.add(`log-cmd-${cmd}`);
  }
  // Create a fresh AnsiToHTML instance per call to avoid stale state from unclosed tags
  const ansi = new AnsiToHTML({ escapeXML: true });
  logMessage.innerHTML = ansi.toHtml(msg);
  return logMessage;
};

export const createLogLine = (line, cmd) => {
  const div = document.createElement("div");
  div.classList.add("job-log-line");
  div._jobLogTime = line.timestamp;

  const lineNumber = document.createElement("div");
  lineNumber.className = "line-num";
  lineNumber.innerText = line.index;
  div.appendChild(lineNumber);

  // TODO: Support displaying time optionally

  div.appendChild(createLogLineMessage(line.message, cmd));

  return div;
};

export function createEmptyActionsRun() {
  return {
    htmlurl: "",
    title: "",
    canCancel: false,
    canDeleteArtifact: false,
    canScrubSummaries: false,
    done: false,
    jobs: [],
    jobSummaries: [],
    runError: "",
    index: 0,
    status: "",
    triggerEvent: "",
    triggeredAt: 0,
    duration: "",
  };
}

// Shared state for the run view: the Summary view polls the run-level
// endpoint through this store; the job view polls the job endpoint itself
// and writes the run-level part of its response back into the store.
export function createActionRunViewStore(actionsURL, runIndex) {
  let loadingAbortController = null;
  let intervalID = null;

  const viewData = reactive({
    currentRun: createEmptyActionsRun(),
    runArtifacts: [],
    loaded: false,
  });

  const loadCurrentRun = async () => {
    if (loadingAbortController) return;
    const abortController = new AbortController();
    loadingAbortController = abortController;
    try {
      const resp = await actionsFetch(
        `${actionsURL}/runs/${runIndex}`,
        JSON.stringify({ logCursors: [] }),
        abortController.signal,
      );
      const runResp = await resp.json();

      // if the request was aborted (stale), ignore the response
      if (abortController.signal.aborted) return;

      viewData.currentRun = runResp.state.run;
      viewData.runArtifacts = runResp.artifacts || [];
      viewData.loaded = true;

      if (viewData.currentRun.done && intervalID) {
        clearInterval(intervalID);
        intervalID = null;
      }
    } catch (e) {
      // ignore network errors while unloading the page and stale aborts
      if (e instanceof TypeError || abortController.signal.aborted) return;
      throw e;
    } finally {
      if (loadingAbortController === abortController) {
        loadingAbortController = null;
      }
    }
  };

  return reactive({
    viewData,

    async startPollingCurrentRun() {
      await loadCurrentRun();
      if (viewData.currentRun.done) return;
      intervalID = setInterval(loadCurrentRun, 1000);
    },
    async forceReloadCurrentRun() {
      loadingAbortController?.abort();
      loadingAbortController = null;
      await loadCurrentRun();
    },
    // Polling stops permanently once the run is done, so an action that brings
    // the run back to life (a job re-run) must reload and restart it explicitly.
    async resumePollingCurrentRun() {
      await this.forceReloadCurrentRun();
      if (!viewData.currentRun.done && !intervalID) {
        intervalID = setInterval(loadCurrentRun, 1000);
      }
    },
    stopPollingCurrentRun() {
      if (!intervalID) return;
      clearInterval(intervalID);
      intervalID = null;
    },
  });
}
